/** The Insights charts' colours for this organization and this theme: the
 *  brand's primary colour leads where it can (chartModel decides, so a pale or
 *  grey brand never makes a chart unreadable), light and dark each get their
 *  own validated steps. Worked out once per brand colour and theme. */
import { useBrand } from '@/brand';
import { useTheme } from '@/hooks/useTheme';
import { OTHER_GREY, categoricalPalette, leadColour, sequentialRamp, type Mode } from './chartModel';

export interface ChartColors {
  mode: Mode;
  /** One-series charts (bars of categories, short answers). */
  lead: string;
  /** Series that must be told apart (Yes / No, categories over time), in fixed order. */
  categorical: string[];
  /** The tail folded into one mark ("Other"). */
  other: string;
  /** `n` steps of the lead hue for magnitude (a score's or a number's columns). */
  ramp: (n: number) => string[];
}

const cache = new Map<string, ChartColors>();

export function useChartColors(): ChartColors {
  const primary = useBrand().colors?.primary;
  const mode: Mode = useTheme().theme === 'dark' ? 'dark' : 'light';
  const key = `${primary}|${mode}`;
  let colors = cache.get(key);
  if (!colors) {
    const lead = leadColour(primary, mode);
    colors = {
      mode, lead, categorical: categoricalPalette(primary, mode), other: OTHER_GREY[mode],
      ramp: (n) => sequentialRamp(lead, n, mode),
    };
    cache.set(key, colors);
  }
  return colors;
}
